import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import ToggleCard from './ToggleCard.tsx';
import SelectField, { Option } from '../../../../core/components/Fields/SelectField.tsx';
import FormField from '../../../../core/components/FormField.tsx';
import { IChannel } from '../../../../document/stores/channelStore.tsx';
import {
    ERP_TOOL_PROVIDER_QUERIES,
    ERP_TOOL_PROVIDER_QUERY_KEYS,
    erpToolProviderTranslationKey,
    NO_INTEGRATIONS_QUERY,
} from '../config/erpToolProviders.ts';
import { DefaultChannelsConfig } from '../config/ConfigSteps.tsx';

// the select shows names only, not the ids underneath
export const renderOptionLabel = (option: Option) => <span className="flex text-content-strong">{option.label}</span>;

export const DEFAULT_CHANNEL_PREFIXES = ['order', 'orderConfirmation', 'rfq', 'invoice', 'deliveryNote'] as const;
export const EMPTY_DEFAULT_CHANNELS = Object.fromEntries(
    DEFAULT_CHANNEL_PREFIXES.map((prefix) => [`${prefix}ChannelId`, ''])
) as Record<string, string>;

export interface ErpToolProviderValue {
    key: string;
    integrationId?: string | null;
}

interface ErpToolProviderCardProps {
    icon?: React.ReactNode;
    providerKey: string;
    value: ErpToolProviderValue | null;
    onChange: (value: { key: string; integrationId: string | null } | null) => void;
    isDisabled?: boolean;
}

/** One ERP the assistant (or a project's chats) can look things up in; when the customer has several connections to
 *  it, a select picks one (optional, like in the agents config). */
export const ErpToolProviderCard = ({
    icon,
    providerKey,
    value,
    onChange,
    isDisabled = false,
}: ErpToolProviderCardProps) => {
    const { t } = useTranslation('assistants');
    const { t: tAgentConfig } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    const isChecked = value !== null;
    const query = ERP_TOOL_PROVIDER_QUERIES[providerKey];
    const { data } = useQuery(query ?? NO_INTEGRATIONS_QUERY, { skip: !isChecked || !query });
    const integrationOptions = (query ? data?.[ERP_TOOL_PROVIDER_QUERY_KEYS[providerKey]] || [] : []).map(
        (integration: { id: string; name: string }) => ({ value: integration.id, label: integration.name })
    );

    const translationKey = erpToolProviderTranslationKey(providerKey);

    return (
        <ToggleCard
            icon={icon}
            title={tAgentConfig(`toolProviders.${translationKey}.name`, { defaultValue: providerKey })}
            description={tAgentConfig(`toolProviders.${translationKey}.description`, { defaultValue: '' })}
            isChecked={isChecked}
            isDisabled={isDisabled}
            onIsCheckedChange={(checked) => onChange(checked ? { key: providerKey, integrationId: null } : null)}
            panel={
                isChecked &&
                integrationOptions.length > 0 && (
                    <FormField label={t('detail.integrationsTab.erp.connectionLabel')}>
                        <SelectField
                            options={integrationOptions}
                            placeholder={t('detail.integrationsTab.erp.connectionPlaceholder')}
                            value={value?.integrationId || undefined}
                            onValueChange={(integrationId) =>
                                onChange({ key: providerKey, integrationId: integrationId || null })
                            }
                            renderOption={renderOptionLabel}
                            disabled={isDisabled}
                            modal
                        />
                    </FormField>
                )
            }
        />
    );
};

interface DefaultChannelsFieldsProps {
    channelData: any;
    value: Partial<DefaultChannelsConfig>;
    onChange: (value: DefaultChannelsConfig) => void;
    isDisabled?: boolean;
}

/** One channel select per document type the chat may create documents for. */
export const DefaultChannelsFields = ({
    channelData,
    value,
    onChange,
    isDisabled = false,
}: DefaultChannelsFieldsProps) => {
    const { t: tAgentConfig } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    return (
        <>
            {DEFAULT_CHANNEL_PREFIXES.map((prefix) => {
                const configKey = `${prefix}ChannelId`;
                const channels: IChannel[] = channelData?.[`${prefix}ProcessingChannels`] || [];
                return (
                    <FormField
                        key={configKey}
                        label={tAgentConfig(`toolProviders.defaultChannels.${prefix}ChannelLabel`)}
                    >
                        <SelectField
                            options={channels.map((channel) => ({ value: channel.id, label: channel.name }))}
                            placeholder={tAgentConfig(`toolProviders.defaultChannels.${prefix}ChannelPlaceholder`)}
                            // null, not '': SelectField only shows its placeholder for null/undefined
                            value={value?.[configKey] || null}
                            onValueChange={(selected) =>
                                onChange({ ...value, [configKey]: selected } as DefaultChannelsConfig)
                            }
                            renderOption={renderOptionLabel}
                            disabled={isDisabled}
                            modal
                        />
                    </FormField>
                );
            })}
        </>
    );
};
